import { useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import { createGradebookBatchRanges } from '@/features/gradebook/gradebookService';

import {
  earnedByUnit,
  isThreadOpen,
  normalizeCoachSection,
  type CoachThread,
  type RawSectionStatLike,
} from './kaiCoachContent';
import {
  coachGoalFromConversation,
  exportCoachDetailedScoresCsv,
  type CoachCsvActivity,
  type CoachCsvGoal,
  type CoachCsvStudent,
} from './kaiCoachDetailedScoresCsv';
import { normalizeMessage, type CoachMessage } from './kaiCoachService';
import type { RawKaiCoachConversation, RawKaiCoachMessage, RawKaiCoachSection } from './kaiCoachTypes';

export interface DownloadCoachDetailedScoresInput {
  courseId: string;
  includeConversations: boolean;
  moduleId: string;
  moduleTitle: string;
  section: RawKaiCoachSection;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

// Legacy Kudu responses may be wrapped in a { Response } envelope.
function unwrapEnvelope(response: unknown): Record<string, unknown> | null {
  if (response && typeof response === 'object') {
    if ('Response' in response) {
      return unwrapEnvelope((response as { Response: unknown }).Response);
    }
    return response as Record<string, unknown>;
  }
  return null;
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

// The display header for an activity column, matching the class-average table.
function threadHeader(thread: CoachThread): string {
  return thread.sessionLabel && thread.sessionLabel !== thread.label
    ? `${thread.sessionLabel} · ${thread.label}`
    : thread.label;
}

// A student's coach section_stats for the exported module (id => earned points).
function readCoachSectionStats(
  rawStudent: Record<string, unknown>,
  moduleId: string,
): RawSectionStatLike[] {
  const components = asArray<Record<string, unknown>>(rawStudent.components);
  const target =
    components.find((comp) => asString(comp.uid ?? comp.id) === moduleId) ??
    components[0];
  return asArray<RawSectionStatLike>(
    target?.section_stats ?? target?.sectionStats,
  );
}

function compareStudents(a: CoachCsvStudent, b: CoachCsvStudent): number {
  const last = a.lastName.trim().toLowerCase().localeCompare(b.lastName.trim().toLowerCase());
  if (last !== 0) {
    return last;
  }
  return a.firstName.trim().toLowerCase().localeCompare(b.firstName.trim().toLowerCase());
}

/**
 * Downloads a per-student CSV of Kai Coach participation scores — one Score
 * column per open activity — and, when the instructor opts in, each activity's
 * conversation transcript in an adjacent column. Students are fetched with the
 * same batched roster read used elsewhere; transcripts come from the
 * instructor-only GetKaiCoachConversations endpoint over the same batches.
 */
export function useDownloadCoachDetailedScores() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getIdToken, user } = useAuth();

  return useMutation<void, ApiError, DownloadCoachDetailedScoresInput>({
    mutationFn: async ({ courseId, includeConversations, moduleId, moduleTitle, section }) => {
      if (!user) {
        throw createDataError('You must be signed in to download scores.');
      }
      const normalized = normalizeCoachSection(section);
      // A goal-reached coach always reads the conversations: when each goal was
      // reached, and Kai's reasons, live there.
      const includeGoals = normalized.creditEnabled && normalized.creditMethod === 'goalReached';
      const readConversations = includeConversations || includeGoals;
      const openThreads = normalized.threads.filter((thread) =>
        isThreadOpen(thread, Date.now()),
      );
      if (openThreads.length === 0) {
        throw createDataError('This coach module has no open activities to export.');
      }
      const kuduUserId = getKuduUserId(user) ?? user.uid;
      if (!kuduUserId) {
        throw createDataError('Unable to determine the current Kudu user ID.');
      }

      const activities: CoachCsvActivity[] = openThreads.map((thread) => ({
        header: threadHeader(thread),
        points: thread.points,
        scoreUnitUid: thread.scoreUnitUid,
      }));
      // Map a returned conversation's (session, activity) back to its scoreUnitUid.
      const unitByThread = new Map<string, string>(
        openThreads.map((thread) => [
          `${thread.sessionUid} ${thread.activityUid}`,
          thread.scoreUnitUid,
        ]),
      );

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });

      // Enrollment count, to size the batches.
      const groupResponse = await client.invokeKuduMethod<unknown, {
        course_id: string;
        is_instructor_request: boolean;
        user_id: string;
      }>('GetCourseStudentGroup', {
        course_id: courseId,
        is_instructor_request: true,
        user_id: kuduUserId,
      });
      const group = (unwrapEnvelope(groupResponse)?.course_student_group ??
        unwrapEnvelope(groupResponse)?.courseStudentGroup ??
        {}) as Record<string, unknown>;
      const studentIds =
        group.course_student_ids ?? group.courseStudentIds ?? group.course_students;
      const studentCount = Array.isArray(studentIds) ? studentIds.length : 0;

      const threadKeys = openThreads.map((thread) => ({
        activity_uid: thread.activityUid,
        session_uid: thread.sessionUid,
      }));

      const students: CoachCsvStudent[] = [];
      for (const range of createGradebookBatchRanges(studentCount)) {
        // Scores: each student's coach section_stats for this module.
        const batchResponse = await client.invokeKuduMethod<unknown, {
          component_ids: string[];
          course_id: string;
          end_index: number;
          start_index: number;
        }>('GetCourseStudents', {
          component_ids: [moduleId],
          course_id: courseId,
          end_index: range.endIndex,
          start_index: range.startIndex,
        });
        const rawStudents = asArray<Record<string, unknown>>(
          unwrapEnvelope(batchResponse)?.course_students ??
            unwrapEnvelope(batchResponse)?.courseStudents,
        );

        // Transcripts (opt-in): the same student slice, keyed by (user, thread).
        const conversationsByUser = new Map<string, Record<string, CoachMessage[]>>();
        const goalsByUser = new Map<string, Record<string, CoachCsvGoal>>();
        if (readConversations) {
          const convResponse = await client.invokeKuduMethod<unknown, {
            course_uid: string;
            end_index: number;
            module_uid: string;
            start_index: number;
            threads: Array<{ activity_uid: string; session_uid: string }>;
            user_id: string;
          }>('GetKaiCoachConversations', {
            course_uid: courseId,
            end_index: range.endIndex,
            module_uid: moduleId,
            start_index: range.startIndex,
            threads: threadKeys,
            user_id: kuduUserId,
          });
          const conversations = asArray<RawKaiCoachConversation>(
            unwrapEnvelope(convResponse)?.conversations ??
              unwrapEnvelope(convResponse)?.Conversations,
          );
          for (const conv of conversations) {
            const userUid = asString(conv.user_uid ?? conv.userUid);
            const unit = unitByThread.get(
              `${asString(conv.session_uid ?? conv.sessionUid)} ${asString(
                conv.activity_uid ?? conv.activityUid,
              )}`,
            );
            if (!userUid || !unit) {
              continue;
            }
            const messages = asArray<RawKaiCoachMessage>(conv.messages).map(
              normalizeMessage,
            );
            const forUser = conversationsByUser.get(userUid) ?? {};
            forUser[unit] = messages;
            conversationsByUser.set(userUid, forUser);
            if (includeGoals) {
              const reachedAt = Number(conv.goal_reached_at_utc_milli ?? conv.goalReachedAtUtcMilli);
              const goals = goalsByUser.get(userUid) ?? {};
              goals[unit] = coachGoalFromConversation(
                messages,
                (conv.goal_reached_at_utc_milli ?? conv.goalReachedAtUtcMilli) && Number.isFinite(reachedAt)
                  ? reachedAt
                  : null,
                asString(conv.goal_reached_message_uid ?? conv.goalReachedMessageUid),
              );
              goalsByUser.set(userUid, goals);
            }
          }
        }

        for (const rawStudent of rawStudents) {
          const profile = (rawStudent.profile ?? {}) as Record<string, unknown>;
          const userId = asString(rawStudent.id ?? rawStudent.uid);
          students.push({
            email: asString(profile.preferred_email ?? profile.preferredEmail),
            firstName: asString(profile.first_name ?? profile.firstName),
            instituteId: asString(
              profile.institute_official_id ?? profile.instituteOfficialId,
            ),
            lastName: asString(profile.last_name ?? profile.lastName),
            earnedByUnit: earnedByUnit(readCoachSectionStats(rawStudent, moduleId)),
            ...(includeConversations
              ? { conversationsByUnit: conversationsByUser.get(userId) ?? {} }
              : {}),
            ...(includeGoals ? { goalsByUnit: goalsByUser.get(userId) ?? {} } : {}),
          });
        }
      }

      students.sort(compareStudents);
      exportCoachDetailedScoresCsv(moduleTitle, activities, students, {
        conversations: includeConversations,
        goals: includeGoals,
      });
    },
  });
}
